import React, { useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import UserAvatar, { getFrameType } from '@entities/user/ui/UserAvatar';
import { useTooltip } from '@app/providers';
import { useUserRoles } from './hooks/useUserRoles';
import { useRatingInfo } from './hooks/useRatingInfo';
import { useCardAnimation } from './hooks/useCardAnimation';
import { formatDate, formatTooltipText } from './utils/cardFormatters';
import { getCardFrameClass } from './utils/cardStyles';
import UserRolesIcons from './components/UserRolesIcons';
import UserStatsCompact from './components/UserStatsCompact';
import CardButton from './components/CardButton';
import PrivateCard from './components/PrivateCard';
import ErrorCard from './components/ErrorCard';
import './UserCard.css';

const UserCard = ({
  user,
  showRole = true,
  showStats = true,
  size = 'medium',
  onClick,
  avatarFrame = null
}) => {
  const navigate = useNavigate();
  const { showTooltip, hideTooltip } = useTooltip();
  const cardRef = useRef(null);

  const userRoles = useUserRoles(user);
  const ratingInfo = useRatingInfo(user?.rating);
  const frameType = getFrameType(user, avatarFrame);
  const cardFrameClass = getCardFrameClass(user, avatarFrame);

  // Анимация карточки
  useCardAnimation(user, avatarFrame, cardRef);

  // Обработчики
  const handleCardClick = useCallback(() => {
    if (onClick) {
      onClick(user);
    } else if (user?.id) {
      navigate(`/profile/${user.id}`);
    }
  }, [onClick, user, navigate]);

  const handleMouseEnter = useCallback(() => {
    const tooltipText = formatTooltipText(user, ratingInfo, userRoles);
    showTooltip(
      tooltipText.title,
      tooltipText.stats,
      tooltipText.bio
    );
  }, [user, ratingInfo, userRoles, showTooltip]);

  // Валидация
  if (!user) {
    return <ErrorCard />;
  }

  if (user.is_public === false) {
    return <PrivateCard ref={cardRef} cardFrameClass={cardFrameClass} />;
  }

  const primaryRole = userRoles[0];

  return (
    <div
      ref={cardRef}
      className={`user-card ${primaryRole?.class || ''} rating-level-${ratingInfo.level} ${cardFrameClass}`}
      onClick={handleCardClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={hideTooltip}
    >
      <div className="user-card-avatar">
        <UserAvatar
          user={user}
          size={size}
          withFrame={true}
          frameType={frameType}
        />
      </div>

      <div className="user-card-info">
        <div className="user-name-line">
          <div className="user-name">{user.display_name || user.username}</div>
        </div>

        {showRole && (
          <UserRolesIcons
            roles={userRoles}
            showTooltip={showTooltip}
            hideTooltip={hideTooltip}
          />
        )}

        {showStats && (
          <UserStatsCompact user={user} ratingInfo={ratingInfo} />
        )}

        {user.last_active && (
          <div
            className="user-last-active-compact"
            title={`Последняя активность: ${formatDate(user.last_active)}`}
          >
            ⏱️ {formatDate(user.last_active)}
          </div>
        )}
      </div>

      <CardButton
        username={user.username}
        showTooltip={showTooltip}
        hideTooltip={hideTooltip}
      />
    </div>
  );
};

export default UserCard;